import React, { useState } from 'react';
import { ArrowLeftIcon, MapPinIcon, DownloadIcon, ShieldCheckIcon, UploadIcon, FileTextIcon, HistoryIcon, CreditCardIcon, GavelIcon, BuildingIcon, AlertCircleIcon, CheckCircleIcon, MapIcon, XIcon, FileIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { samplePlots } from '../data/sampleData';
export function PlotDetailsPage({
  plotId,
  navigateTo
}: {
  plotId: string | null;
  navigateTo: (page: string) => void;
}) {
  const {
    language,
    t
  } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [showUploadUI, setShowUploadUI] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState(['Original Deed.pdf', 'Mutation Order.pdf', 'Tax Receipt 2025.pdf']);
  const plot = samplePlots.find(p => p.id === plotId) || samplePlots[0];
  const handleUploadClick = () => {
    setShowUploadUI(true);
    setUploadProgress(0);
  };
  const simulateUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileName = e.target.files[0].name;
      let progress = 0;
      const interval = setInterval(() => {
        progress += 10;
        setUploadProgress(progress);
        if (progress >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setUploadedFiles([fileName, ...uploadedFiles]);
            setShowUploadUI(false);
          }, 500);
        }
      }, 100);
    }
  };
  const tabs = [{
    id: 'overview',
    label: t('tabOverview'),
    icon: FileTextIcon
  }, {
    id: 'clo',
    label: t('tabCLO'),
    icon: ShieldCheckIcon
  }, {
    id: 'mutation',
    label: t('tabMutation'),
    icon: HistoryIcon
  }, {
    id: 'tax',
    label: t('tabTax'),
    icon: CreditCardIcon
  }, {
    id: 'parcelMap',
    label: t('tabParcelMap') || 'Parcel Map',
    icon: MapIcon
  }, {
    id: 'transferHistory',
    label: t('tabTransferHistory') || 'Transfer History',
    icon: HistoryIcon
  }, {
    id: 'disputes',
    label: t('tabDisputes'),
    icon: GavelIcon
  }, {
    id: 'mortgages',
    label: t('tabMortgages'),
    icon: BuildingIcon
  }, {
    id: 'documents',
    label: t('tabDocuments'),
    icon: UploadIcon
  }];
  return <div className="space-y-6 pb-10" data-id="element-1839">
      <button onClick={() => navigateTo('myPlots')} className="flex items-center text-sm font-medium text-gray-500 hover:text-[#006A4E] transition-colors" data-id="element-1840">
        <ArrowLeftIcon className="w-4 h-4 mr-1" data-id="element-1841" /> Back to My Plots
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 relative overflow-hidden" data-id="element-1842">
        {plot.dispute && <div className="absolute top-0 left-0 w-full h-1 bg-red-500" data-id="element-1843"></div>}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4" data-id="element-1844">
          <div className="flex items-center space-x-4" data-id="element-1845">
            <div className="w-16 h-16 bg-[#006A4E] rounded-xl flex items-center justify-center shadow-inner flex-shrink-0" data-id="element-1846">
              <MapPinIcon className="w-8 h-8 text-white" data-id="element-1847" />
            </div>
            <div data-id="element-1848">
              <div className="flex items-center space-x-2 mb-1" data-id="element-1849">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-1850">
                  {language === 'bn' ? plot.nameBn : plot.name}
                </h1>
                {plot.dispute && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200" data-id="element-1851">
                    <AlertCircleIcon className="w-3 h-3 mr-1" data-id="element-1852" />
                    Disputed
                  </span>}
              </div>
              <p className="text-sm text-gray-500 flex items-center" data-id="element-1853">
                <span className="font-medium text-gray-700 mr-2" data-id="element-1854">
                  ID: {plot.id}
                </span>
                <span className="mx-2 text-gray-300" data-id="element-1855">|</span>
                {language === 'bn' ? plot.districtBn : plot.district},{' '}
                {language === 'bn' ? plot.upazilaBn : plot.upazila}
              </p>
            </div>
          </div>
          <div className="flex space-x-3 w-full md:w-auto" data-id="element-1856">
            <button onClick={() => navigateTo('cloCertificate')} className="flex-1 md:flex-none px-6 py-2.5 bg-[#006A4E] text-white rounded-lg font-bold hover:bg-[#004D3A] transition-colors text-sm flex items-center justify-center shadow-sm" data-id="element-1857">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1858" /> {t('downloadCLO')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col md:flex-row min-h-[500px]" data-id="element-1859">
        {/* Vertical Tabs (Desktop) / Horizontal Scroll (Mobile) */}
        <div className="w-full md:w-64 bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200 flex-shrink-0" data-id="element-1860">
          <div className="flex md:flex-col overflow-x-auto md:overflow-visible hide-scrollbar p-2 md:p-4 gap-1" data-id="element-1861">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? 'bg-[#006A4E] text-white shadow-md' : 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'}`} data-id="element-1862">
                  <Icon className={`w-5 h-5 ${isActive ? 'text-green-100' : 'text-gray-400'}`} data-id="element-1863" />
                  <span data-id="element-1864">{tab.label}</span>
                </button>;
          })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-1865">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in duration-300" data-id="element-1866">
              <div className="space-y-8" data-id="element-1867">
                <div data-id="element-1868">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1869">
                    <MapPinIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1870" />
                    {t('locationInfo')}
                  </h3>
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-4" data-id="element-1871">
                    <div className="grid grid-cols-2 gap-4" data-id="element-1872">
                      <div data-id="element-1873">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1874">
                          {t('district')}
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-1875">
                          {language === 'bn' ? plot.districtBn : plot.district}
                        </p>
                      </div>
                      <div data-id="element-1876">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1877">
                          {t('upazila')}
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-1878">
                          {language === 'bn' ? plot.upazilaBn : plot.upazila}
                        </p>
                      </div>
                      <div className="col-span-2" data-id="element-1879">
                        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1880">
                          {t('mouza')}
                        </p>
                        <p className="font-semibold text-gray-900" data-id="element-1881">
                          {language === 'bn' ? plot.mouzaBn : plot.mouza}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div data-id="element-1882">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1883">
                    <FileTextIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1884" />
                    Record Details
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1885">
                    <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100" data-id="element-1886">
                      <p className="text-xs text-blue-600 uppercase tracking-wider mb-1" data-id="element-1887">
                        {t('khatian')}
                      </p>
                      <p className="text-xl font-bold text-blue-900" data-id="element-1888">
                        {plot.khatian}
                      </p>
                    </div>
                    <div className="bg-purple-50/50 rounded-xl p-4 border border-purple-100" data-id="element-1889">
                      <p className="text-xs text-purple-600 uppercase tracking-wider mb-1" data-id="element-1890">
                        {t('dag')}
                      </p>
                      <p className="text-xl font-bold text-purple-900" data-id="element-1891">
                        {plot.dag}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8" data-id="element-1892">
                <div data-id="element-1893">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center border-b border-gray-100 pb-2" data-id="element-1894">
                    <ShieldCheckIcon className="w-5 h-5 mr-2 text-[#006A4E]" data-id="element-1895" />
                    Property Status
                  </h3>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100" data-id="element-1896">
                    <div className="p-4 flex justify-between items-center" data-id="element-1897">
                      <span className="text-gray-600 font-medium" data-id="element-1898">
                        {t('landType')}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-semibold capitalize" data-id="element-1899">
                        {t(plot.type as any)}
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1900">
                      <span className="text-gray-600 font-medium" data-id="element-1901">
                        {t('area')}
                      </span>
                      <span className="font-bold text-gray-900" data-id="element-1902">
                        {plot.area} Decimals
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1903">
                      <span className="text-gray-600 font-medium" data-id="element-1904">
                        {t('ownershipType')}
                      </span>
                      <span className="font-semibold text-gray-900 capitalize" data-id="element-1905">
                        {t(plot.ownership as any)}
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1906">
                      <span className="text-gray-600 font-medium" data-id="element-1907">
                        {t('mutationStatus')}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold flex items-center ${plot.mutationStatus === 'completed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-1908">
                        {plot.mutationStatus === 'completed' && <CheckCircleIcon className="w-3.5 h-3.5 mr-1" data-id="element-1909" />}
                        {t(plot.mutationStatus as any)}
                      </span>
                    </div>
                    <div className="p-4 flex justify-between items-center" data-id="element-1910">
                      <span className="text-gray-600 font-medium" data-id="element-1911">
                        {t('taxStatus')}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${plot.taxStatus === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1912">
                        {t(plot.taxStatus as any)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {/* MUTATION INFO TAB */}
          {activeTab === 'mutation' && <div className="animate-in fade-in duration-300" data-id="element-1913">
              <div className="flex justify-between items-center mb-6" data-id="element-1914">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1915">
                  {t('tabMutation') || 'Mutation Info'}
                </h3>
                <button className="px-4 py-2 bg-[#006A4E] text-white rounded-lg text-sm font-medium hover:bg-[#004D3A] transition-colors shadow-sm" data-id="element-1916">
                  {t('applyMutation')}
                </button>
              </div>

              {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="space-y-6" data-id="element-1917">
                  {plot.transferHistory.map((record: any, idx: number) => <div key={record.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1918">
                      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex justify-between items-center" data-id="element-1919">
                        <span className="font-bold text-gray-700 flex items-center" data-id="element-1920">
                          <FileTextIcon className="w-4 h-4 mr-2 text-[#006A4E]" data-id="element-1921" />
                          Record #{idx + 1}
                        </span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1922">
                          {language === 'bn' ? record.typeBn : record.type}
                        </span>
                      </div>
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1923">
                        <div data-id="element-1924">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1925">
                            {t('transferDate')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1926">
                            {record.date}
                          </p>
                        </div>
                        <div data-id="element-1927">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1928">
                            {t('transferFrom')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1929">
                            {record.from}
                          </p>
                        </div>
                        <div data-id="element-1930">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1931">
                            {t('transferTo')}
                          </p>
                          <p className="font-medium text-[#006A4E]" data-id="element-1932">
                            {record.to}
                          </p>
                        </div>
                        <div data-id="element-1933">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1934">
                            {t('transferType')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1935">
                            {language === 'bn' ? record.typeBn : record.type}
                          </p>
                        </div>
                        <div data-id="element-1936">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1937">
                            {t('mutCaseNo') || 'Case No.'}
                          </p>
                          <p className="font-mono font-medium text-gray-900" data-id="element-1938">
                            {record.caseNo || record.dolilNo || record.docRef}
                          </p>
                        </div>
                        <div data-id="element-1939">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1940">
                            {t('mutCaseDate') || 'Case Date'}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1941">
                            {record.caseDate || record.dolilDate || record.date}
                          </p>
                        </div>
                        <div data-id="element-1942">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1943">
                            {t('approvalDate') || 'Approval Date'}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1944">
                            {record.approvalDate || '-'}
                          </p>
                        </div>
                        <div data-id="element-1945">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1946">
                            {t('dcrNo') || 'DCR No.'}
                          </p>
                          <p className="font-mono font-medium text-gray-900" data-id="element-1947">
                            {record.dcrNo || '-'}
                          </p>
                        </div>
                        <div data-id="element-1948">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1949">
                            {t('dcrDate') || 'DCR Date'}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-1950">
                            {record.dcrDate || '-'}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1951">
                  <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1952" />
                  <p className="text-gray-500 font-medium" data-id="element-1953">
                    {t('noRecordFound')}
                  </p>
                </div>}
            </div>}

          {/* TAX HISTORY TAB */}
          {activeTab === 'tax' && <div className="animate-in fade-in duration-300" data-id="element-1954">
              <div className="flex justify-between items-center mb-6" data-id="element-1955">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1956">
                  {t('ldTaxHistory')}
                </h3>
                <button className="px-4 py-2 bg-[#006A4E] text-white rounded-lg text-sm font-medium hover:bg-[#004D3A] transition-colors shadow-sm" data-id="element-1957">
                  {t('payTax')}
                </button>
              </div>

              {plot.taxHistory && plot.taxHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-1958">
                  <div className="overflow-x-auto" data-id="element-1959">
                    <table className="w-full text-left text-sm" data-id="element-1960">
                      <thead className="bg-gray-50 text-gray-600 font-medium border-b border-gray-200" data-id="element-1961">
                        <tr data-id="element-1962">
                          <th className="px-6 py-4" data-id="element-1963">{t('taxYear')}</th>
                          <th className="px-6 py-4" data-id="element-1964">{t('amount')}</th>
                          <th className="px-6 py-4" data-id="element-1965">{t('status')}</th>
                          <th className="px-6 py-4" data-id="element-1966">{t('receiptNo')}</th>
                          <th className="px-6 py-4" data-id="element-1967">{t('paymentDate')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-1968">
                        {plot.taxHistory.map((record: any) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-1969">
                            <td className="px-6 py-4 font-medium text-gray-900" data-id="element-1970">
                              {record.year}
                            </td>
                            <td className="px-6 py-4 font-bold text-gray-700" data-id="element-1971">
                              {record.amount}
                            </td>
                            <td className="px-6 py-4" data-id="element-1972">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1973">
                                {t(record.status as any)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-500 font-mono text-xs" data-id="element-1974">
                              {record.receiptNo}
                            </td>
                            <td className="px-6 py-4 text-gray-500" data-id="element-1975">
                              {record.date}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-1976">
                  <CreditCardIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1977" />
                  <p className="text-gray-500 font-medium" data-id="element-1978">
                    {t('noRecordFound')}
                  </p>
                </div>}
            </div>}

          {/* PARCEL MAP TAB */}
          {activeTab === 'parcelMap' && <div className="animate-in fade-in duration-300" data-id="element-1979">
              <div className="flex justify-between items-center mb-6" data-id="element-1980">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-1981">
                  {t('parcelMap') || 'Parcel Map'}
                </h3>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm" data-id="element-1982">
                <div className="border border-gray-200 rounded-lg overflow-hidden mb-6 bg-white flex justify-center" data-id="element-1983">
                  <img src="https://cdn.magicpatterns.com/uploads/ny2k3HFhyHRGZ8XpcGKfEL/Parcel-map-data.jpg" alt="Parcel Map" className="max-w-full h-auto object-contain max-h-[600px]" data-id="element-1984" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-5 rounded-xl border border-gray-100" data-id="element-1985">
                  <div data-id="element-1986">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1987">
                      {t('khatian')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1988">
                      {plot.khatian}
                    </p>
                  </div>
                  <div data-id="element-1989">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1990">
                      {t('dag')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1991">
                      {plot.dag}
                    </p>
                  </div>
                  <div data-id="element-1992">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1993">
                      {t('mouza')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1994">
                      {language === 'bn' ? plot.mouzaBn : plot.mouza}
                    </p>
                  </div>
                  <div data-id="element-1995">
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-1996">
                      {t('area')}
                    </p>
                    <p className="text-lg font-bold text-gray-900" data-id="element-1997">
                      {plot.area} Decimals
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {/* TRANSFER HISTORY TAB */}
          {activeTab === 'transferHistory' && <div className="animate-in fade-in duration-300" data-id="element-1998">
              <div className="flex justify-between items-center mb-6" data-id="element-1999">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-2000">
                  {t('tabTransferHistory') || 'Transfer History'}
                </h3>
              </div>

              {plot.transferHistory && plot.transferHistory.length > 0 ? <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2001">
                  <div className="bg-[#4CAF50] text-white text-center py-3 font-bold text-lg" data-id="element-2002">
                    {t('transferStatement') || 'হস্তান্তর বৃত্তান্ত'}
                  </div>
                  <div className="overflow-x-auto" data-id="element-2003">
                    <table className="w-full text-center text-sm border-collapse" data-id="element-2004">
                      <thead className="bg-gray-50 text-gray-800 font-medium" data-id="element-2005">
                        <tr data-id="element-2006">
                          <th className="px-4 py-3 border border-gray-200" data-id="element-2007">
                            {t('prevKhatianNo') || 'আগত খতিয়ান নং'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-2008">
                            {t('dag') || 'দাগ নং'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-2009">
                            {t('dagLandArea') || 'দাগে জমির পরিমান (একর)'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-2010">
                            {t('khatianLandArea') || 'খতিয়ানে জমির পরিমাণ (একর)'}
                          </th>
                          <th className="px-4 py-3 border border-gray-200" data-id="element-2011">
                            {t('transferredLandArea') || 'হস্তান্তরিত জমির পরিমাণ (একর)'}
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-2012">
                        {plot.transferHistory.map((record: any, idx: number) => <tr key={record.id} className="hover:bg-gray-50 transition-colors" data-id="element-2013">
                              <td className="px-4 py-3 border border-gray-200 font-medium text-gray-900" data-id="element-2014">
                                {record.prevKhatianNo || '-'}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-2015">
                                {record.dagNo || plot.dag}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-2016">
                                {record.dagLandArea || '-'}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 text-gray-700" data-id="element-2017">
                                {record.khatianLandArea || '-'}
                              </td>
                              <td className="px-4 py-3 border border-gray-200 font-bold text-gray-900" data-id="element-2018">
                                {record.transferredLandArea || '-'}
                              </td>
                            </tr>)}
                      </tbody>
                    </table>
                  </div>
                  <div className="border-t border-gray-200" data-id="element-2019">
                    <div className="flex border-b border-gray-200" data-id="element-2020">
                      <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-2021">
                        {t('ownershipSource') || 'মালিকানা সূত্র'}
                      </div>
                      <div className="w-2/3 md:w-3/4 p-4 text-gray-900 font-medium flex items-center" data-id="element-2022">
                        {language === 'bn' ? (plot.transferHistory[0] as any).ownershipSourceBn || `${plot.transferHistory[0].typeBn} সূত্রেঃ` : (plot.transferHistory[0] as any).ownershipSource || `By ${plot.transferHistory[0].type}:`}
                      </div>
                    </div>
                    <div className="flex" data-id="element-2023">
                      <div className="w-1/3 md:w-1/4 p-4 border-r border-gray-200 font-bold text-gray-800 flex items-center justify-center text-center" data-id="element-2024">
                        {t('descriptionLabel') || 'বিবরণ'}
                      </div>
                      <div className="w-2/3 md:w-3/4 p-4 text-gray-500 tracking-widest flex items-center" data-id="element-2025">
                        ------------------------
                      </div>
                    </div>
                  </div>
                </div> : <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-2026">
                  <HistoryIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2027" />
                  <p className="text-gray-500 font-medium" data-id="element-2028">
                    {t('noRecordFound')}
                  </p>
                </div>}
            </div>}

          {/* LITIGATIONS / DISPUTES TAB */}
          {activeTab === 'disputes' && <div className="animate-in fade-in duration-300" data-id="element-2029">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2030">
                {t('litigations')}
              </h3>

              {plot.litigations && plot.litigations.length > 0 ? <div className="space-y-4" data-id="element-2031">
                  {plot.litigations.map((lit: any) => <div key={lit.id} className="bg-white border border-red-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2032">
                      <div className="bg-red-50 px-6 py-3 border-b border-red-100 flex justify-between items-center" data-id="element-2033">
                        <div className="flex items-center space-x-2" data-id="element-2034">
                          <AlertCircleIcon className="w-5 h-5 text-red-600" data-id="element-2035" />
                          <span className="font-bold text-red-900" data-id="element-2036">
                            {lit.caseNo}
                          </span>
                        </div>
                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-bold" data-id="element-2037">
                          {language === 'bn' ? lit.statusBn : lit.status}
                        </span>
                      </div>
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2038">
                        <div data-id="element-2039">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-2040">
                            {t('court')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-2041">
                            {language === 'bn' ? lit.courtBn : lit.court}
                          </p>
                        </div>
                        <div data-id="element-2042">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-2043">
                            {t('dateFiled')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-2044">
                            {lit.dateFiled}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-2045">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2046">
                            {t('plaintiff')}
                          </p>
                          <p className="font-bold text-gray-900" data-id="element-2047">
                            {language === 'bn' ? lit.plaintiffBn : lit.plaintiff}
                          </p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-2048">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-2049">
                            {t('defendant')}
                          </p>
                          <p className="font-bold text-gray-900" data-id="element-2050">
                            {language === 'bn' ? lit.defendantBn : lit.defendant}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-16 bg-green-50 rounded-xl border border-green-200" data-id="element-2051">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2052">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-2053" />
                  </div>
                  <h4 className="text-lg font-bold text-green-900 mb-2" data-id="element-2054">
                    Clear Title
                  </h4>
                  <p className="text-green-700" data-id="element-2055">
                    No active litigations or disputes found for this property.
                  </p>
                </div>}
            </div>}

          {/* MORTGAGES TAB */}
          {activeTab === 'mortgages' && <div className="animate-in fade-in duration-300" data-id="element-2056">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2057">
                {t('mortgages')}
              </h3>

              {plot.mortgages && plot.mortgages.length > 0 ? <div className="space-y-4" data-id="element-2058">
                  {plot.mortgages.map((mortgage: any) => <div key={mortgage.id} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-2059">
                      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2060">
                        <div className="flex items-center space-x-3" data-id="element-2061">
                          <BuildingIcon className="w-5 h-5 text-gray-500" data-id="element-2062" />
                          <span className="font-bold text-gray-900 text-lg" data-id="element-2063">
                            {language === 'bn' ? mortgage.bankBn : mortgage.bank}
                          </span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${mortgage.status === 'active' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-2064">
                          {t(mortgage.status as any)}
                        </span>
                      </div>
                      <div className="p-6 grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-2065">
                        <div data-id="element-2066">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-2067">
                            {t('loanAmount')}
                          </p>
                          <p className="text-xl font-bold text-gray-900" data-id="element-2068">
                            {mortgage.amount}
                          </p>
                        </div>
                        <div data-id="element-2069">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-2070">
                            {t('mortgageDate')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-2071">
                            {mortgage.date}
                          </p>
                        </div>
                        <div data-id="element-2072">
                          <p className="text-sm text-gray-500 mb-1" data-id="element-2073">
                            {t('releaseDate')}
                          </p>
                          <p className="font-medium text-gray-900" data-id="element-2074">
                            {mortgage.releaseDate}
                          </p>
                        </div>
                      </div>
                    </div>)}
                </div> : <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200 border-dashed" data-id="element-2075">
                  <BuildingIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-2076" />
                  <p className="text-gray-500 font-medium" data-id="element-2077">
                    No mortgage records found.
                  </p>
                </div>}
            </div>}

          {/* CLO TAB */}
          {activeTab === 'clo' && <div className="flex flex-col items-center justify-center py-16 space-y-6 animate-in fade-in duration-300" data-id="element-2078">
              <div className="w-32 h-32 bg-green-50 rounded-full flex items-center justify-center border-4 border-green-100" data-id="element-2079">
                <ShieldCheckIcon className="w-16 h-16 text-[#006A4E]" data-id="element-2080" />
              </div>
              <div className="text-center" data-id="element-2081">
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2082">
                  Certificate of Land Ownership
                </h3>
                <p className="text-lg text-gray-500 font-mono bg-gray-100 px-4 py-1 rounded-md inline-block" data-id="element-2083">
                  {plot.cloId || 'Not Generated'}
                </p>
              </div>
              <button onClick={() => navigateTo('cloCertificate')} className="mt-4 px-8 py-3 bg-[#006A4E] text-white rounded-lg font-bold hover:bg-[#004D3A] transition-colors shadow-md hover:shadow-lg transform hover:-translate-y-0.5" data-id="element-2084">
                View Full Certificate
              </button>
            </div>}

          {/* DOCUMENTS TAB */}
          {activeTab === 'documents' && <div className="space-y-6 animate-in fade-in duration-300" data-id="element-2085">
              <div className="flex justify-between items-center" data-id="element-2086">
                <h3 className="text-xl font-bold text-gray-900" data-id="element-2087">
                  Related Documents
                </h3>
                <button onClick={handleUploadClick} className="px-4 py-2 bg-[#006A4E] text-white rounded-lg font-medium hover:bg-[#004D3A] transition-colors text-sm flex items-center shadow-sm" data-id="element-2088">
                  <UploadIcon className="w-4 h-4 mr-2" data-id="element-2089" />
                  {t('uploadDoc')}
                </button>
              </div>

              {/* Upload UI */}
              {showUploadUI && <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center relative animate-in slide-in-from-top-4" data-id="element-2090">
                  <button onClick={() => setShowUploadUI(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-id="element-2091">
                    <XIcon className="w-5 h-5" data-id="element-2092" />
                  </button>

                  {uploadProgress > 0 ? <div className="max-w-md mx-auto" data-id="element-2093">
                      <FileIcon className="w-12 h-12 text-[#006A4E] mx-auto mb-4" data-id="element-2094" />
                      <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-2095">
                        Uploading document...
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-2096">
                        <div className="bg-[#006A4E] h-2.5 rounded-full transition-all duration-300" style={{
                  width: `${uploadProgress}%`
                }} data-id="element-2097"></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2" data-id="element-2098">
                        {uploadProgress}% Complete
                      </p>
                    </div> : <div data-id="element-2099">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-gray-200" data-id="element-2100">
                        <UploadIcon className="w-8 h-8 text-[#006A4E]" data-id="element-2101" />
                      </div>
                      <h4 className="text-lg font-bold text-gray-900 mb-1" data-id="element-2102">
                        Click to upload or drag and drop
                      </h4>
                      <p className="text-sm text-gray-500 mb-6" data-id="element-2103">
                        PDF, JPG, PNG up to 10MB
                      </p>

                      <label className="cursor-pointer px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors shadow-sm inline-block" data-id="element-2104">
                        Select File
                        <input type="file" className="hidden" onChange={simulateUpload} accept=".pdf,.jpg,.jpeg,.png" data-id="element-2105" />
                      </label>
                    </div>}
                </div>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2106">
                {uploadedFiles.map((doc, i) => <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-[#006A4E] cursor-pointer transition-all group" data-id="element-2107">
                    <div className="flex items-center space-x-4" data-id="element-2108">
                      <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors" data-id="element-2109">
                        <FileTextIcon className="w-6 h-6 text-blue-600" data-id="element-2110" />
                      </div>
                      <div data-id="element-2111">
                        <p className="text-sm font-bold text-gray-900 group-hover:text-[#006A4E] transition-colors" data-id="element-2112">
                          {doc}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-2113">
                          Added on{' '}
                          {i === 0 && doc !== 'Original Deed.pdf' ? 'Just now' : 'Jan 15, 2026'}{' '}
                          • 2.4 MB
                        </p>
                      </div>
                    </div>
                    <button className="p-2 text-gray-400 hover:text-[#006A4E] hover:bg-green-50 rounded-full transition-colors" data-id="element-2114">
                      <DownloadIcon className="w-5 h-5" data-id="element-2115" />
                    </button>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
    </div>;
}